/**
 * FilingActionRow — single row in the "Filings needing your action" card.
 *
 * Row contents, left → right:
 *   - Building name (semibold)
 *   - BBL (monospace, muted) — from `building.bbl` if present, else first
 *     external id that looks BBL-shaped, else just the building id.
 *   - <StatusBadge /> (Layer-1)
 *   - Ownership fragment ("Waiting on: you" / "RDP" / "DOB")
 *   - Deadline (red <7d, amber <30d, neutral otherwise, dash if none)
 *   - CTA Link (or <StartFilingButton /> for unstarted)
 *
 * Responsive: on sm and below, we stack the row into two lines to avoid the
 * name truncating; the CTA drops to the right of the second line.
 */

import React from "react";
import { Link } from "@tanstack/react-router";

import { StartFilingButton } from "@/pages/BuildingsList/StartFilingButton";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/shared/ui/button";
import { cn } from "@/lib/utils";
import {
  deriveOwnership,
  type Jurisdiction,
  type Ownership,
} from "@/components/common/statusTaxonomy";
import type { Filing } from "@/api/user";

import { filingDeadline } from "./useDashboardMetrics";

interface FilingActionRowProps {
  filing: Filing;
  jurisdiction: Jurisdiction;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const buildingIdOf = (f: Filing): string | null => {
  if (!f.building) return null;
  if (typeof f.building === "string") return f.building;
  return f.building.id ?? null;
};

const buildingNameOf = (f: Filing): string => {
  if (!f.building) return "Building";
  if (typeof f.building === "string") return "Building";
  return f.building.name ?? "Building";
};

/**
 * Attempt to pull a BBL from whatever we have on the filing's building.
 * The aggregate response doesn't promise this — we render "—" if absent.
 */
const bblOf = (f: Filing): string | null => {
  if (!f.building || typeof f.building === "string") return null;
  const b = f.building as Record<string, unknown>;
  if (typeof b.bbl === "string" && b.bbl.length) return b.bbl;
  const ext = b.externalIds as
    | Array<{ source?: string; externalId?: string }>
    | undefined;
  if (Array.isArray(ext)) {
    const hit = ext.find(
      (e) => e.source?.toLowerCase?.() === "bbl" && e.externalId,
    );
    if (hit?.externalId) return hit.externalId;
  }
  return null;
};

const ownershipLabel = (owner: Ownership): string => {
  switch (owner) {
    case "owner":
      return "Waiting on: You";
    case "rdp":
      return "Waiting on: RDP";
    case "dob":
      return "Waiting on: NYC DOB";
    default:
      return "";
  }
};

const routeFor = (f: Filing, type: Jurisdiction): string => {
  const buildingId = buildingIdOf(f);
  if (!buildingId) return "/buildings";
  if (type === "ll97") {
    if (f.status === 0) return `/buildings/${buildingId}/`;
    return `/buildings/${buildingId}/ll97/${f.id}`;
  }
  return `/buildings/${buildingId}/ll88/${f.id}`;
};

export const FilingActionRow: React.FC<FilingActionRowProps> = ({
  filing,
  jurisdiction,
}) => {
  const buildingId = buildingIdOf(filing);
  const bbl = bblOf(filing);
  const owner = deriveOwnership(filing.status, { jurisdiction });

  const deadline = filingDeadline(filing);
  let deadlineCls = "text-muted-foreground";
  let deadlineText = "—";
  if (deadline) {
    const days = Math.ceil((deadline.getTime() - Date.now()) / DAY_MS);
    if (days < 0) {
      deadlineCls = "[color:hsl(var(--danger))] font-medium";
      deadlineText = `Overdue ${Math.abs(days)}d`;
    } else if (days < 7) {
      deadlineCls = "[color:hsl(var(--danger))] font-medium";
      deadlineText = `${days}d left`;
    } else if (days < 30) {
      deadlineCls = "[color:hsl(var(--warning))] font-medium";
      deadlineText = `${days}d left`;
    } else {
      deadlineCls = "text-muted-foreground";
      deadlineText = `${days}d left`;
    }
  }

  const jurLabel = jurisdiction === "ll97" ? "LL97" : "LL88";

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-md border border-border bg-card p-4",
        "md:flex-row md:items-center md:gap-4",
      )}
    >
      {/* Identity block */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-semibold text-foreground">
            {buildingNameOf(filing)}
          </span>
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {jurLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-mono tabular-nums text-muted-foreground">
            {bbl ?? "BBL: —"}
          </span>
          {owner !== "none" && (
            <>
              <span aria-hidden="true" className="text-muted-foreground">
                ·
              </span>
              <span className="text-muted-foreground">
                {ownershipLabel(owner)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Status + deadline */}
      <div className="flex items-center gap-4 md:gap-6">
        <StatusBadge
          code={filing.status}
          jurisdiction={jurisdiction}
          compact
          className="shrink-0"
        />
        <span
          className={cn("shrink-0 text-sm tabular-nums", deadlineCls)}
          aria-label={deadline ? `Deadline ${deadlineText}` : "No deadline set"}
        >
          {deadlineText}
        </span>
      </div>

      {/* CTA */}
      <div className="flex shrink-0 items-center md:ml-auto">
        {filing.status === 0 && buildingId ? (
          <StartFilingButton
            buildingId={buildingId}
            building={filing.building as any}
            filingType={jurLabel as "LL97" | "LL88"}
            className="h-9"
          />
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link to={routeFor(filing, jurisdiction)}>Open</Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilingActionRow;
